import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import { RouteSeo } from "@/lib/seo";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { RequireAuth } from "@/components/RequireAuth";
import { CookieBanner } from "@/components/CookieBanner";
import { BottomTabBar } from "@/components/BottomTabBar";
import Home from "@/pages/Home";

const Discover = lazy(() => import("@/pages/Discover"));
const PlaceDetail = lazy(() => import("@/pages/PlaceDetail"));
const Events = lazy(() => import("@/pages/Events"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const MapPage = lazy(() => import("@/pages/MapPage"));
const WeatherPage = lazy(() => import("@/pages/WeatherPage"));
const SubmitEvent = lazy(() => import("@/pages/SubmitEvent"));
const MyEvents = lazy(() => import("@/pages/MyEvents"));
const Admin = lazy(() => import("@/pages/Admin"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const SignInPage = lazy(() => import("@/pages/AuthPages").then((m) => ({ default: m.SignInPage })));
const SignUpPage = lazy(() => import("@/pages/AuthPages").then((m) => ({ default: m.SignUpPage })));
const Impressum = lazy(() => import("@/pages/Impressum"));
const Datenschutz = lazy(() => import("@/pages/Datenschutz"));
const Nutzungsbedingungen = lazy(() => import("@/pages/Nutzungsbedingungen"));
const Barrierefreiheit = lazy(() => import("@/pages/Barrierefreiheit"));
const Quellen = lazy(() => import("@/pages/Quellen"));
const Kontakt = lazy(() => import("@/pages/Kontakt"));
const Islands = lazy(() => import("@/pages/Islands"));
const IslandDetail = lazy(() => import("@/pages/IslandDetail"));
const Tides = lazy(() => import("@/pages/Tides"));
const Arrival = lazy(() => import("@/pages/Arrival"));
const DeichRad = lazy(() => import("@/pages/DeichRad"));
const Bathing = lazy(() => import("@/pages/Bathing"));
const Towns = lazy(() => import("@/pages/Towns"));
const TownDetail = lazy(() => import("@/pages/TownDetail"));
const Themes = lazy(() => import("@/pages/Themes"));
const ThemeEbike = lazy(() => import("@/pages/ThemeEbike"));
const ThemeDog = lazy(() => import("@/pages/ThemeDog"));
const ThemeAccess = lazy(() => import("@/pages/ThemeAccess"));
const TourDetail = lazy(() => import("@/pages/TourDetail"));
const Weekend = lazy(() => import("@/pages/Weekend"));
const Newsletter = lazy(() => import("@/pages/Newsletter"));
const WattGuides = lazy(() => import("@/pages/WattGuides"));
const ThemeCanoe = lazy(() => import("@/pages/ThemeCanoe"));
const ThemeCamping = lazy(() => import("@/pages/ThemeCamping"));
const Account = lazy(() => import("@/pages/Account"));
const PromotionSuccess = lazy(() => import("@/pages/PromotionSuccess"));

// /orte/:id is shared: place ids are UUIDs, everything else is a town slug
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OrteDetail = () => { const { id } = useParams(); return UUID_RE.test(id) ? <PlaceDetail /> : <TownDetail />; };

const PageLoading = () => (
  <div className="flex min-h-[60vh] items-center justify-center" data-testid="page-loading">
    <span className="h-8 w-8 animate-spin rounded-full border-2 border-ocean/20 border-t-ocean" />
  </div>
);

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Shell = () => {
  const { pathname } = useLocation();
  const bare = pathname.startsWith("/karte") || pathname.startsWith("/sign-");
  return (
    <div className="flex min-h-screen flex-col pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
      <Header />
      <main className="flex-1">
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/entdecken" element={<Discover />} />
            <Route path="/entdecken/themen" element={<Themes />} />
            <Route path="/entdecken/themen/e-bike" element={<ThemeEbike />} />
            <Route path="/entdecken/themen/hund" element={<ThemeDog />} />
            <Route path="/entdecken/themen/barrierefrei" element={<ThemeAccess />} />
            <Route path="/entdecken/themen/kanu" element={<ThemeCanoe />} />
            <Route path="/entdecken/themen/camping" element={<ThemeCamping />} />
            <Route path="/entdecken/touren/:id" element={<TourDetail />} />
            <Route path="/veranstaltungen" element={<Events />} />
            <Route path="/wochenende" element={<Weekend />} />
            <Route path="/newsletter" element={<Newsletter />} />
            <Route path="/wattfuehrer" element={<WattGuides />} />
            <Route path="/veranstaltungen/:id" element={<EventDetail />} />
            <Route path="/merkliste" element={<Favorites />} />
            <Route path="/karte" element={<MapPage />} />
            <Route path="/wetter" element={<WeatherPage />} />
            <Route path="/einreichen" element={<RequireAuth><SubmitEvent /></RequireAuth>} />
            <Route path="/meine-veranstaltungen" element={<RequireAuth><MyEvents /></RequireAuth>} />
            <Route path="/hervorheben/erfolg" element={<PromotionSuccess />} />
            <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
            <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />
            <Route path="/quellen" element={<Quellen />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/inseln" element={<Islands />} />
            <Route path="/inseln/:slug" element={<IslandDetail />} />
            <Route path="/gezeiten" element={<Tides />} />
            <Route path="/anreise" element={<Arrival />} />
            <Route path="/deich-rad" element={<DeichRad />} />
            <Route path="/badestellen" element={<Bathing />} />
            <Route path="/orte" element={<Towns />} />
            <Route path="/orte/:id" element={<OrteDetail />} />
            <Route path="/konto" element={<RequireAuth><Account /></RequireAuth>} />
          </Routes>
        </Suspense>
      </main>
      {!bare && <Footer />}
      <BottomTabBar />
      <CookieBanner />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <ScrollTop />
          <RouteSeo />
          <Shell />
          <Toaster position="bottom-right" mobileOffset={{ bottom: "calc(4.5rem + env(safe-area-inset-bottom))" }} toastOptions={{ style: { borderRadius: 2, fontFamily: "Manrope" } }} />
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
